import { format, parseISO } from 'date-fns'

import { getObjectsSum, isValueNumberType } from '../../utils/common'
import { formatNumberWithSeparators } from '../../utils/number'

import { BIRDVIEW_AGE_RANGE_LAST_DAY_OFFSET } from './DashboardBirdview.constants'
import {
  BIRDVIEW_CHART_DATE_FORMAT,
  BIRDVIEW_CHART_DATE_RANGE_SEPARATOR,
  BirdviewAnalyticsPreset,
  BirdviewKpiUnit,
  BirdviewTrend,
} from './DashboardBirdview.constants.charts'
import {
  BIRDVIEW_CHART_BARS_DEFAULT_ROW_SPAN,
  BIRDVIEW_CHART_BARS_LIMIT,
  BIRDVIEW_CHART_PERIOD_OPTIONS,
  BirdviewChartMetric,
} from './DashboardBirdview.constants.controls'

function checkHasBirdviewChartItemValue(item) {
  return item.value > 0
}

// An average under a tenth of a day comes rounded to 0 and still has a bar.
function checkHasBirdviewChartItemRate(item) {
  return isValueNumberType(item.value)
}

export function checkIsBirdviewCountMetric(metric) {
  return metric === BirdviewChartMetric.Count
}

export function checkIsBirdviewPerUnitsMetric(metric) {
  return metric === BirdviewChartMetric.PerUnits
}

export function checkIsBirdviewAvgTimeMetric(metric) {
  return metric === BirdviewChartMetric.AvgTime
}

export function checkIsBirdviewPersonalPreset(preset) {
  return preset === BirdviewAnalyticsPreset.Personal
}

export function formatBirdviewChartDate(date) {
  return format(parseISO(date), BIRDVIEW_CHART_DATE_FORMAT)
}

export function formatBirdviewChartDateRange({ date, dateTo }) {
  return date === dateTo
    ? formatBirdviewChartDate(date)
    : [date, dateTo]
        .map(formatBirdviewChartDate)
        .join(BIRDVIEW_CHART_DATE_RANGE_SEPARATOR)
}

export function getBirdviewTrend(delta) {
  if (delta > 0) {
    return BirdviewTrend.Up
  }

  if (delta < 0) {
    return BirdviewTrend.Down
  }

  return BirdviewTrend.Flat
}

// Counts get thousands separators; a rate keeps the decimal point it comes with.
export function formatBirdviewNumber(value) {
  return Number.isInteger(value) ? formatNumberWithSeparators(value) : value
}

export function formatBirdviewKpiValue({ t, unit, value }) {
  const number = formatBirdviewNumber(value)

  switch (unit) {
    case BirdviewKpiUnit.Days:
      return t('DashboardBirdview.RelativeTimeDaysKey', { count: number })
    case BirdviewKpiUnit.Hours:
      return t('DashboardBirdview.RelativeTimeHoursKey', { count: number })
    case BirdviewKpiUnit.Percent:
      return `${number}%`
    default:
      return String(number)
  }
}

export function toBirdviewTileDelta({ delta, t, unit }) {
  return isValueNumberType(delta)
    ? {
        direction: getBirdviewTrend(delta),
        text: formatBirdviewKpiValue({ t, unit, value: Math.abs(delta) }),
      }
    : null
}

export function getBirdviewChartBarsLimit({ defaultLimit, rowSpan }) {
  return rowSpan > BIRDVIEW_CHART_BARS_DEFAULT_ROW_SPAN
    ? BIRDVIEW_CHART_BARS_LIMIT
    : defaultLimit
}

export function getBirdviewChartPeriodDays(period) {
  return BIRDVIEW_CHART_PERIOD_OPTIONS.find(option => option.value === period)
    .days
}

// An all-zero chart reads as empty.
export function toBirdviewChartSection(items) {
  const shown = items.filter(checkHasBirdviewChartItemValue)

  return { items: shown, total: getObjectsSum(shown) }
}

export function toBirdviewMetricChartSection({ items, metric }) {
  const shown = items.filter(
    checkIsBirdviewCountMetric(metric)
      ? checkHasBirdviewChartItemValue
      : checkHasBirdviewChartItemRate
  )

  return { items: shown, metric, total: getObjectsSum(shown) }
}

export function toBirdviewSeriesSection(points) {
  return { items: points, total: points.length }
}

// The upper bound is exclusive, so the label names the last day still covered.
export function getBirdviewAgeRangeLabelParams({ maxDays, minDays }) {
  return {
    ...(maxDays ? { max: maxDays - BIRDVIEW_AGE_RANGE_LAST_DAY_OFFSET } : null),
    min: minDays,
  }
}
